(() => {
  const GRID_STORAGE_KEY = 'dik-grid-columns';
  const GRID_STORAGE_KEY_DESKTOP = 'dik-grid-columns-desktop';
  const FILTER_PARAM = 'filter.p.product_type';
  const DENSITY_SCOPES = {
    mobile: {storageKey: GRID_STORAGE_KEY, gridAttr: 'data-dik-columns', values: [1, 2]},
    desktop: {storageKey: GRID_STORAGE_KEY_DESKTOP, gridAttr: 'data-dik-desktop-columns', values: [3, 4, 5]},
  };
  let quickBuyScrollLockCount = 0;
  let quickBuyScrollPosition = 0;

  const lockQuickBuyScroll = () => {
    if (quickBuyScrollLockCount === 0) {
      quickBuyScrollPosition = window.scrollY;
      document.body.style.setProperty('--dik-quick-buy-scroll-offset', `${-quickBuyScrollPosition}px`);
      document.documentElement.classList.add('dik-quick-buy-scroll-locked');
      document.body.classList.add('dik-quick-buy-scroll-locked');
    }

    quickBuyScrollLockCount += 1;
  };

  const unlockQuickBuyScroll = () => {
    if (quickBuyScrollLockCount === 0) return;

    quickBuyScrollLockCount -= 1;
    if (quickBuyScrollLockCount > 0) return;

    document.documentElement.classList.remove('dik-quick-buy-scroll-locked');
    document.body.classList.remove('dik-quick-buy-scroll-locked');
    document.body.style.removeProperty('--dik-quick-buy-scroll-offset');
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, quickBuyScrollPosition);
    document.documentElement.style.scrollBehavior = previousScrollBehavior;
  };

  const readMotionDuration = (property, fallback) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
    const raw = getComputedStyle(document.documentElement).getPropertyValue(property).trim();
    const value = Number.parseFloat(raw);
    if (!Number.isFinite(value)) return fallback;
    return raw.endsWith('s') && !raw.endsWith('ms') ? value * 1000 : value;
  };

  const formatMoney = (cents) => {
    const format = window.themeMoneyFormat || '${{amount}}';
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return format;

    const withDelimiters = (amount, decimals, thousands, decimalMark) => {
      const fixed = (amount / 100).toFixed(decimals);
      const [whole, fraction] = fixed.split('.');
      const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${groupedWhole}${decimalMark}${fraction}` : groupedWhole;
    };

    let amount;
    switch (placeholder[1]) {
      case 'amount_no_decimals':
        amount = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        amount = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(placeholder[0], amount);
  };

  const toHandle = (value) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  const readGridColumns = (scope) => {
    const {storageKey, values} = DENSITY_SCOPES[scope];
    try {
      const value = Number(window.localStorage.getItem(storageKey));
      if (values.includes(value)) return value;
      return scope === 'mobile' ? 1 : null;
    } catch (_error) {
      return scope === 'mobile' ? 1 : null;
    }
  };

  const writeGridColumns = (columns, scope) => {
    try {
      window.localStorage.setItem(DENSITY_SCOPES[scope].storageKey, String(columns));
    } catch (_error) {
      // Storage can be unavailable in private browsing; the control still works for this page.
    }
  };

  const syncDensityControls = (columns, scope) => {
    document.querySelectorAll('dik-catalog-navigator').forEach((navigator) => {
      navigator.setDensity(columns, false, scope);
    });
  };

  class DikCatalogNavigator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;

      this.dataset.ready = 'true';
      this.gridId = this.dataset.gridId;
      this.sourceUrl = this.dataset.sourceUrl;
      this.historyHash = this.dataset.historyHash || '';
      this.strings = {
        loading: this.dataset.statusLoading,
        empty: this.dataset.statusEmpty,
        countOne: this.dataset.statusCountOne,
        countOther: this.dataset.statusCountOther,
      };
      this.status = this.querySelector('[data-catalog-status]');
      this.filterBar = this.querySelector('.t-tabs');
      this.filterPill = this.querySelector('.t-tabs-pill');
      this.moreButton = this.querySelector('[data-catalog-more]');
      this.abortController = null;
      this.onPopState = this.onPopState.bind(this);
      this.onFilterKeydown = this.onFilterKeydown.bind(this);
      this.onFilterScroll = () => this.updateMoreIndicator();
      this.onResize = () => {
        const active = this.querySelector('[data-filter-value][aria-selected="true"]');
        if (active) this.moveFilterPill(active, false);
        this.syncGridFillers();
        this.updateMoreIndicator();
        this.scheduleVisibilityUpdate();
      };

      this.querySelectorAll('[data-filter-value]').forEach((link) => {
        link.addEventListener('click', (event) => this.onFilterClick(event));
      });
      this.filterBar?.addEventListener('keydown', this.onFilterKeydown);
      this.filterBar?.addEventListener('scroll', this.onFilterScroll, {passive: true});
      if ('ResizeObserver' in window && this.filterBar) {
        this.moreResizeObserver = new ResizeObserver(() => this.updateMoreIndicator());
        this.moreResizeObserver.observe(this.filterBar);
        this.querySelectorAll('[data-filter-value]').forEach((filter) => this.moreResizeObserver.observe(filter));
      }
      this.moreButton?.addEventListener('click', () => {
        // Reveal exactly the next tab that's cut off, rather than a fixed
        // fraction of the bar's width — the latter jumps further than a
        // drag would and can skip a whole tab.
        const viewportRight = this.filterBar.scrollLeft + this.filterBar.clientWidth;
        const nextTab = [...this.querySelectorAll('[data-filter-value]')].find(
          (tab) => tab.offsetLeft + tab.offsetWidth > viewportRight + 1,
        );
        const distance = nextTab
          ? nextTab.offsetLeft - this.filterBar.scrollLeft
          : Math.max(120, this.filterBar.clientWidth * 0.62);
        this.filterBar.scrollBy({left: distance, behavior: 'smooth'});
      });

      this.querySelectorAll('[data-grid-columns]').forEach((button) => {
        button.addEventListener('click', () => {
          const scope = button.closest('[data-density-scope]')?.dataset.densityScope || 'mobile';
          const columns = Number(button.dataset.gridColumns);
          writeGridColumns(columns, scope);
          syncDensityControls(columns, scope);
          this.clampScrollToCatalog();
        });
      });

      const initialFilter = new URL(window.location.href).searchParams.get(FILTER_PARAM) || '';
      this.restoreDensities();
      this.setActiveFilter(initialFilter);
      this.observeGrid();
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('resize', this.onResize);

      if (initialFilter && this.historyHash) {
        this.applyLocalFilter(initialFilter, false);
      }
      window.requestAnimationFrame(() => this.updateMoreIndicator());
      document.fonts?.ready.then(() => this.updateMoreIndicator());
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('resize', this.onResize);
      this.filterBar?.removeEventListener('keydown', this.onFilterKeydown);
      this.filterBar?.removeEventListener('scroll', this.onFilterScroll);
      window.removeEventListener('scroll', this.onVisibilityScroll);
      this.visibilityResizeObserver?.disconnect();
      this.moreResizeObserver?.disconnect();
      this.abortController?.abort();
      window.cancelAnimationFrame(this.scrollClampFrame);
      window.cancelAnimationFrame(this.visibilityFrame);
    }

    get grid() {
      return document.getElementById(this.gridId);
    }

    get activeGridColumns() {
      const grid = this.grid;
      if (!grid) return 1;

      if (window.matchMedia('(min-width: 990px)').matches) {
        return Number(grid.getAttribute(DENSITY_SCOPES.desktop.gridAttr)) || 4;
      }

      return Number(grid.getAttribute(DENSITY_SCOPES.mobile.gridAttr)) || 1;
    }

    syncGridFillers() {
      const grid = this.grid;
      if (!grid) return;

      grid.querySelectorAll(':scope > .grid__item--empty').forEach((item) => item.remove());

      const visibleProducts = grid.querySelectorAll(':scope > .grid__item:not([hidden])');
      if (!visibleProducts.length) return;

      const columns = this.activeGridColumns;
      const remainder = visibleProducts.length % columns;
      const fillersNeeded = remainder === 0 ? 0 : columns - remainder;

      for (let index = 0; index < fillersNeeded; index += 1) {
        const filler = document.createElement('li');
        filler.className = 'grid__item grid__item--empty';
        filler.setAttribute('aria-hidden', 'true');
        grid.append(filler);
      }
    }

    setDensity(columns, persist = true, scope = 'mobile') {
      const config = DENSITY_SCOPES[scope];
      if (!config || !config.values.includes(columns)) return;

      this.grid?.setAttribute(config.gridAttr, String(columns));
      this.syncGridFillers();
      this.querySelectorAll(`[data-density-scope="${scope}"] [data-grid-columns]`).forEach((button) => {
        const isActive = Number(button.dataset.gridColumns) === columns;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      if (persist) writeGridColumns(columns, scope);
    }

    clampScrollToCatalog() {
      const host = this.closest('.dik-catalog-host') || this.grid?.closest('.dik-catalog-host');
      if (!host) return;

      window.cancelAnimationFrame(this.scrollClampFrame);
      this.scrollClampFrame = window.requestAnimationFrame(() => {
        const hostBottom = window.scrollY + host.getBoundingClientRect().bottom;
        const maxCatalogScroll = Math.max(0, hostBottom - window.innerHeight);
        if (window.scrollY <= maxCatalogScroll + 1) return;

        const previousScrollBehavior = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollTo(0, maxCatalogScroll);
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
      });
    }

    // No stored desktop preference yet: mark the button matching the viewport's
    // natural column count so the control isn't out of sync with the grid,
    // without forcing a --dik-desktop-columns override onto narrower desktop widths.
    markDefaultDesktopDensity() {
      // The grid itself is 4-col at every desktop width unless a density
      // button sets a --dik-desktop-columns override (dik-concept.css no
      // longer switches to 5 at 1440px on its own), so the resting default
      // is always 4 here too — otherwise this marks "5" active while the
      // grid is actually showing 4.
      const columns = 4;
      this.querySelectorAll('[data-density-scope="desktop"] [data-grid-columns]').forEach((button) => {
        const isActive = Number(button.dataset.gridColumns) === columns;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    restoreDensities() {
      this.setDensity(readGridColumns('mobile'), false, 'mobile');
      const desktopColumns = readGridColumns('desktop');
      if (desktopColumns) {
        this.setDensity(desktopColumns, false, 'desktop');
      } else {
        this.markDefaultDesktopDensity();
      }
    }

    updateMoreIndicator() {
      if (!this.filterBar || !this.moreButton) return;
      const remaining = this.filterBar.scrollWidth - this.filterBar.clientWidth - this.filterBar.scrollLeft;
      const hasMore = remaining > 2;
      this.moreButton.setAttribute('aria-hidden', String(!hasMore));
      this.moreButton.tabIndex = hasMore ? 0 : -1;
    }

    moveFilterPill(tab, animate) {
      if (!this.filterPill || !tab) return;

      if (!animate) {
        const previousTransition = this.filterPill.style.transition;
        this.filterPill.style.transition = 'none';
        this.filterPill.style.transform = `translateX(${tab.offsetLeft}px)`;
        this.filterPill.style.width = `${tab.offsetWidth}px`;
        void this.filterPill.offsetWidth;
        this.filterPill.style.transition = previousTransition;
        return;
      }

      this.filterPill.style.transform = `translateX(${tab.offsetLeft}px)`;
      this.filterPill.style.width = `${tab.offsetWidth}px`;
    }

    setActiveFilter(value) {
      let activeLink = null;
      this.querySelectorAll('[data-filter-value]').forEach((link) => {
        const isActive = link.dataset.filterValue === value;
        link.classList.toggle('is-active', isActive);
        link.setAttribute('aria-selected', String(isActive));
        link.tabIndex = isActive ? 0 : -1;
        if (isActive) {
          activeLink = link;
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (activeLink) {
        const animate = this.dataset.filterPillReady === 'true';
        window.requestAnimationFrame(() => this.moveFilterPill(activeLink, animate));
        this.dataset.filterPillReady = 'true';
      }
    }

    observeGrid() {
      this.visibilityHost = this.closest('.dik-catalog-host') || this.grid;
      if (!this.visibilityHost) {
        this.dataset.visible = 'true';
        return;
      }

      this.onVisibilityScroll = () => this.scheduleVisibilityUpdate();
      window.addEventListener('scroll', this.onVisibilityScroll, {passive: true});
      this.visibilityRestingBottom = Number.parseFloat(getComputedStyle(this).bottom) || 0;

      if ('ResizeObserver' in window) {
        this.visibilityResizeObserver = new ResizeObserver(() => this.scheduleVisibilityUpdate());
        this.visibilityResizeObserver.observe(this.visibilityHost);
      }

      this.scheduleVisibilityUpdate();
    }

    scheduleVisibilityUpdate() {
      if (!this.visibilityHost || this.visibilityFrame) return;

      this.visibilityFrame = window.requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      if (!this.visibilityHost) return;

      const rect = this.visibilityHost.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const visibleTop = Math.max(0, rect.top);
      const visibleBottom = Math.min(viewportHeight, rect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      const offsetProperty = '--dik-catalog-nav-section-offset';
      const collisionGap = 12;
      const navHeight = this.offsetHeight;
      const restingTop = viewportHeight - this.visibilityRestingBottom - navHeight;
      const sectionTop = rect.top + collisionGap;
      const sectionBottom = rect.bottom - collisionGap - navHeight;
      const constrainedTop = Math.max(sectionTop, Math.min(restingTop, sectionBottom));
      const sectionOffset = restingTop - constrainedTop;
      this.style.setProperty(offsetProperty, `${sectionOffset}px`);

      /* Behave like a fixed bar through the middle of the catalog, but remain
         contained by both section edges. It enters attached below the top
         boundary and leaves attached above the bottom boundary. */
      this.dataset.visible = String(visibleHeight > 0);
    }

    onFilterClick(event) {
      const link = event.currentTarget;
      if (!(link instanceof HTMLAnchorElement)) return;

      event.preventDefault();
      if (this.historyHash) {
        this.applyLocalFilter(link.dataset.filterValue || '', true);
        return;
      }
      this.loadFilter(link.dataset.filterValue || '', true, link.href);
    }

    onFilterKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const tabs = [...this.querySelectorAll('[data-filter-value]')];
      if (!tabs.length) return;
      const currentIndex = Math.max(0, tabs.indexOf(document.activeElement));
      let nextIndex = currentIndex;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      event.preventDefault();
      tabs[nextIndex].focus();
      tabs[nextIndex].click();
    }

    onPopState() {
      const value = new URL(window.location.href).searchParams.get(FILTER_PARAM) || '';
      if (this.historyHash) {
        this.applyLocalFilter(value, false);
        return;
      }
      const fallbackUrl = new URL(this.sourceUrl, window.location.origin);
      if (value) fallbackUrl.searchParams.set(FILTER_PARAM, value);
      this.loadFilter(value, false, fallbackUrl.toString());
    }

    applyLocalFilter(value, pushState) {
      const grid = this.grid;
      if (!grid) return;

      const requestedType = toHandle(value);
      let count = 0;
      grid.querySelectorAll(':scope > .grid__item--empty').forEach((item) => item.remove());
      grid.querySelectorAll(':scope > .grid__item').forEach((item) => {
        const visible = !requestedType || item.dataset.productType === requestedType;
        item.hidden = !visible;
        if (visible) count += 1;
      });

      this.setActiveFilter(value);
      this.restoreDensities();
      if (pushState) this.updateHistory(value);
      if (this.status) this.status.textContent = this.countMessage(count);
      // A filter can hide most of the grid; if that leaves the page
      // scrolled past the catalog's new (shorter) bottom edge, pull it
      // back up instead of leaving the viewport stranded over the footer.
      this.clampScrollToCatalog();
    }

    countMessage(count) {
      const template = count === 1 ? this.strings.countOne : this.strings.countOther;
      return template.replace('%COUNT%', String(count));
    }

    updateHistory(value) {
      const nextUrl = new URL(window.location.href);
      if (value) nextUrl.searchParams.set(FILTER_PARAM, value);
      else nextUrl.searchParams.delete(FILTER_PARAM);
      if (this.historyHash) nextUrl.hash = this.historyHash;
      window.history.pushState({productType: value}, '', nextUrl);
    }

    async loadFilter(value, pushState, requestUrl) {
      const grid = this.grid;
      if (!grid) return;

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setActiveFilter(value);
      this.setAttribute('aria-busy', 'true');
      grid.classList.add('is-loading');
      if (this.status) this.status.textContent = this.strings.loading;

      try {
        const response = await fetch(requestUrl, {
          headers: {'X-Requested-With': 'XMLHttpRequest'},
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);

        const documentHtml = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sourceGrid = documentHtml.querySelector('#product-grid');
        if (!sourceGrid) throw new Error('Catalog grid missing from response');

        if (value) {
          const requestedType = toHandle(value);
          sourceGrid.querySelectorAll(':scope > .grid__item').forEach((item) => {
            if (item.dataset.productType !== requestedType) item.remove();
          });
        }

        if (!sourceGrid.querySelector(':scope > .grid__item')) {
          const emptyItem = document.createElement('li');
          emptyItem.className = 'dik-catalog-empty';
          emptyItem.setAttribute('role', 'status');
          emptyItem.textContent = this.strings.empty;
          sourceGrid.replaceChildren(emptyItem);
        }

        grid.innerHTML = sourceGrid.innerHTML;
        this.restoreDensities();

        if (pushState) this.updateHistory(value);

        if (this.status) {
          const count = grid.querySelectorAll(':scope > .grid__item:not(.grid__item--empty)').length;
          this.status.textContent = this.countMessage(count);
        }

        this.clampScrollToCatalog();
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(requestUrl);
      } finally {
        this.removeAttribute('aria-busy');
        grid.classList.remove('is-loading');
      }
    }
  }

  class DikLocalization extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.toggle = this.querySelector('[data-localization-toggle]');
      this.panel = this.querySelector('[data-localization-panel]');
      this.search = this.querySelector('[data-localization-search]');
      this.options = Array.from(this.querySelectorAll('[data-country-code]'));
      this.emptyMessage = this.querySelector('[data-localization-empty]');
      this.resultsStatus = this.querySelector('[data-localization-results-count]');
      this.input = this.querySelector('input[name="country_code"]');
      this.form = this.querySelector('form');
      this.closeTimer = null;
      this.activeOption = null;
      if (!this.toggle || !this.panel || !this.input || !this.form) return;

      this.toggle.addEventListener('click', () => this.setOpen(this.panel.hidden));
      this.options.forEach((option) => {
        option.addEventListener('click', () => this.selectOption(option));
        option.addEventListener('mouseenter', () => this.setActiveOption(option));
      });

      if (this.search) {
        this.search.addEventListener('input', () => this.filterOptions());
        this.search.addEventListener('keydown', (event) => this.onSearchKeyDown(event));
      }

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.setOpen(false);
          this.toggle.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      });
    }

    setOpen(open) {
      window.clearTimeout(this.closeTimer);
      this.toggle.setAttribute('aria-expanded', String(open));

      if (open) {
        this.panel.hidden = false;
        this.panel.classList.remove('is-closing');
        window.requestAnimationFrame(() => this.panel.classList.add('is-open'));
        if (this.search) {
          this.search.value = '';
          this.filterOptions();
          this.search.focus();
        }
        return;
      }

      if (this.panel.hidden) return;
      this.panel.classList.remove('is-open');
      this.panel.classList.add('is-closing');
      this.setActiveOption(null);
      this.closeTimer = window.setTimeout(() => {
        this.panel.classList.remove('is-closing');
        this.panel.hidden = true;
      }, readMotionDuration('--dropdown-close-dur', 150));
    }

    visibleOptions() {
      return this.options.filter((option) => !option.hidden);
    }

    filterOptions() {
      const query = this.search.value.trim().toLowerCase();
      let visibleCount = 0;
      this.options.forEach((option) => {
        const matches = !query || option.dataset.search.includes(query);
        option.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      if (this.emptyMessage) this.emptyMessage.hidden = visibleCount !== 0;
      if (this.resultsStatus) this.resultsStatus.textContent = this.countMessage(visibleCount);

      const visible = this.visibleOptions();
      this.setActiveOption(visible.includes(this.activeOption) ? this.activeOption : visible[0] || null);
    }

    countMessage(count) {
      const template = count === 1 ? this.dataset.resultsOne : this.dataset.resultsOther;
      return template ? template.replace('%COUNT%', String(count)) : '';
    }

    setActiveOption(option) {
      if (this.activeOption) this.activeOption.classList.remove('is-focused');
      this.activeOption = option || null;
      if (this.search) this.search.removeAttribute('aria-activedescendant');
      if (!this.activeOption) return;
      this.activeOption.classList.add('is-focused');
      this.activeOption.scrollIntoView({block: 'nearest'});
      if (this.search) this.search.setAttribute('aria-activedescendant', this.activeOption.id);
    }

    onSearchKeyDown(event) {
      const visible = this.visibleOptions();
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const index = visible.indexOf(this.activeOption);
        this.setActiveOption(visible[Math.min(index + 1, visible.length - 1)] || visible[0]);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const index = visible.indexOf(this.activeOption);
        this.setActiveOption(visible[Math.max(index - 1, 0)] || visible[0]);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.activeOption) this.selectOption(this.activeOption);
      } else if (event.key === 'Escape') {
        event.stopPropagation();
        this.setOpen(false);
        this.toggle.focus();
      }
    }

    selectOption(option) {
      this.input.value = option.dataset.countryCode;
      this.form.submit();
    }
  }

  class DikQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;

      this.dataset.ready = 'true';
      this.hasVariants = this.hasAttribute('data-has-variants');
      this.toggleButton = this.querySelector('[data-quick-add-toggle]');
      this.optionsPanel = this.querySelector('[data-quick-add-options]');
      this.variantInput = this.querySelector('[data-quick-add-variant]');
      this.quantityInput = this.querySelector('[data-quick-add-quantity]');
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('.dik-quick-add__submit');
      this.icon = this.querySelector('[data-quick-add-icon]');
      this.closeTimer = null;

      this.quickBuy = this.querySelector('[data-quick-buy]');
      this.quickBuyQty = this.quickBuy?.querySelector('[data-quick-buy-qty]');
      this.quickBuyTotal = this.quickBuy?.querySelector('[data-quick-buy-total]');
      this.quickBuyVariantRow = this.quickBuy?.querySelector('[data-quick-buy-variant-row]');
      this.quickBuyVariantLabel = this.quickBuy?.querySelector('[data-quick-buy-variant-label]');
      this.quickBuyMedia = this.quickBuy?.querySelector('.dik-quick-buy__media');
      this.quickBuyZoomExit = this.quickBuy?.querySelector('[data-quick-buy-unzoom]');
      this.quickBuyCloseTimer = null;
      this.zoomReturnTarget = null;
      this.zoomHistoryPushed = false;
      this.quantity = 1;
      this.unitPrice = 0;

      if (!this.toggleButton || !this.variantInput || !this.form || !this.submitButton || !this.quickBuy) return;

      // Card media clips overflow for the hover-zoom effect, which also clips a
      // fixed-position descendant. Move the sheet to <body> so it isn't clipped
      // and its z-index is compared against top-level siblings (header, drawers).
      document.body.appendChild(this.quickBuy);

      this.toggleButton.addEventListener('click', (event) => {
        event.stopPropagation();
        if (this.hasVariants) {
          this.setOpen(!this.hasAttribute('open'), event.detail === 0);
        } else {
          this.openQuickBuy({
            variantId: this.variantInput.value,
            title: this.toggleButton.dataset.quickAddVariantTitle,
            price: this.toggleButton.dataset.quickAddVariantPrice,
          });
        }
      });

      if (this.hasVariants && this.optionsPanel) {
        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.setOpen(false);
        };
        this.onKeyDown = (event) => {
          if (event.key !== 'Escape' || !this.hasAttribute('open')) return;
          event.preventDefault();
          this.setOpen(false);
          this.toggleButton.focus();
        };
        this.querySelectorAll('[data-quick-add-variant-id]').forEach((button) => {
          button.addEventListener('click', () => this.selectVariant(button));
        });
        this.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onDocumentClick);

        // The options row can be narrower than its option list on dense
        // grids (a 2-column mobile card, say) — fade whichever edge still
        // has more sizes hidden behind a horizontal scroll.
        this.updateOptionsFade = () => {
          const {scrollLeft, scrollWidth, clientWidth} = this.optionsPanel;
          this.optionsPanel.classList.toggle('has-more-start', scrollLeft > 1);
          this.optionsPanel.classList.toggle('has-more-end', scrollLeft + clientWidth < scrollWidth - 1);
        };
        this.optionsPanel.addEventListener('scroll', this.updateOptionsFade, {passive: true});
        if ('ResizeObserver' in window) {
          this.optionsResizeObserver = new ResizeObserver(this.updateOptionsFade);
          this.optionsResizeObserver.observe(this.optionsPanel);
        }
      }

      this.quickBuy.querySelectorAll('[data-quick-buy-close]').forEach((button) => {
        button.addEventListener('click', () => this.setQuickBuyOpen(false));
      });
      this.onQuickBuyMediaClick = (event) => {
        const trigger = event.target.closest('[data-quick-buy-zoom]');
        if (!trigger || !this.quickBuyMedia?.contains(trigger)) return;
        event.preventDefault();
        this.setMediaExpanded(!this.quickBuy.classList.contains('is-media-expanded'), trigger);
      };
      this.quickBuyMedia?.addEventListener('click', this.onQuickBuyMediaClick);
      this.quickBuyZoomExit?.addEventListener('click', () => this.setMediaExpanded(false));
      this.quickBuy.querySelector('[data-quick-buy-increment]')?.addEventListener('click', () => this.changeQuantity(1));
      this.quickBuy.querySelector('[data-quick-buy-decrement]')?.addEventListener('click', () => this.changeQuantity(-1));
      this.quickBuy.querySelector('[data-quick-buy-checkout]')?.addEventListener('click', () => this.checkout());
      this.onQuickBuyKeyDown = (event) => {
        if (this.quickBuy.hidden) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          if (this.quickBuy.classList.contains('is-media-expanded')) {
            this.setMediaExpanded(false);
            return;
          }
          this.setQuickBuyOpen(false);
          return;
        }

        if (event.key === 'Tab') {
          const focusable = Array.from(
            this.quickBuy.querySelectorAll(
              'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => !el.closest('[hidden]'));
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      };
      document.addEventListener('keydown', this.onQuickBuyKeyDown);

      // The zoomed image has no URL of its own, but mobile users reach for the
      // hardware/gesture back button anyway. Without a history entry to absorb
      // that, back navigates the whole page away instead of just closing the zoom.
      this.onQuickBuyPopState = () => {
        if (!this.zoomHistoryPushed) return;
        this.zoomHistoryPushed = false;
        this.setMediaExpanded(false, null, true, {skipHistory: true});
      };
      window.addEventListener('popstate', this.onQuickBuyPopState);
    }

    disconnectedCallback() {
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      if (this.onQuickBuyKeyDown) document.removeEventListener('keydown', this.onQuickBuyKeyDown);
      if (this.onQuickBuyPopState) window.removeEventListener('popstate', this.onQuickBuyPopState);
      if (this.onQuickBuyMediaClick) this.quickBuyMedia?.removeEventListener('click', this.onQuickBuyMediaClick);
      if (this.updateOptionsFade) this.optionsPanel?.removeEventListener('scroll', this.updateOptionsFade);
      this.optionsResizeObserver?.disconnect();
      window.clearTimeout(this.quickBuyCloseTimer);
      if (this.zoomHistoryPushed) {
        // An AJAX re-render (e.g. switching catalog filters) can tear this
        // card down while its zoom is still open. Consume the pending
        // history entry now so it doesn't sit there as an invisible step
        // that later throws off the browser's back/forward button.
        this.zoomHistoryPushed = false;
        window.history.back();
      }
      if (this.isQuickBuyScrollLocked) {
        unlockQuickBuyScroll();
        this.isQuickBuyScrollLocked = false;
      }
      if (this.quickBuy && this.quickBuy.parentElement === document.body) this.quickBuy.remove();
    }

    setOpen(open, moveFocus = false) {
      window.clearTimeout(this.closeTimer);
      if (open) {
        document.querySelectorAll('dik-quick-add[open]').forEach((quickAdd) => {
          if (quickAdd !== this) quickAdd.setOpen(false);
        });
      }

      this.toggleAttribute('open', open);
      this.toggleButton.setAttribute('aria-expanded', String(open));
      if (this.icon) this.icon.dataset.state = open ? 'b' : 'a';

      if (open) {
        this.optionsPanel.hidden = false;
        this.optionsPanel.classList.remove('is-closing');
        window.requestAnimationFrame(() => this.optionsPanel.classList.add('is-open'));
      } else if (!this.optionsPanel.hidden) {
        this.optionsPanel.classList.remove('is-open');
        this.optionsPanel.classList.add('is-closing');
        this.closeTimer = window.setTimeout(() => {
          this.optionsPanel.classList.remove('is-closing');
          this.optionsPanel.hidden = true;
        }, readMotionDuration('--dropdown-close-dur', 150));
      }

      if (open && moveFocus) {
        window.requestAnimationFrame(() => {
          this.optionsPanel.querySelector('button:not(:disabled)')?.focus();
        });
      }
    }

    selectVariant(button) {
      if (button.disabled) return;

      this.setOpen(false);
      this.openQuickBuy({
        variantId: button.dataset.quickAddVariantId,
        title: button.dataset.quickAddVariantTitle,
        price: button.dataset.quickAddVariantPrice,
      });
    }

    openQuickBuy({variantId, title, price}) {
      this.variantInput.value = variantId;
      this.unitPrice = Number(price) || 0;
      this.quantity = 1;
      if (this.quantityInput) this.quantityInput.value = String(this.quantity);
      if (this.quickBuyQty) this.quickBuyQty.textContent = String(this.quantity);

      if (this.quickBuyVariantRow) {
        if (this.hasVariants && title) {
          this.quickBuyVariantRow.hidden = false;
          if (this.quickBuyVariantLabel) this.quickBuyVariantLabel.textContent = title.replace(/ \/ /g, ' · ');
        } else {
          this.quickBuyVariantRow.hidden = true;
        }
      }

      this.updateQuickBuyTotal();
      this.setQuickBuyOpen(true);
    }

    changeQuantity(delta) {
      const next = this.quantity + delta;
      if (next < 1) {
        this.setQuickBuyOpen(false);
        return;
      }

      this.quantity = next;
      if (this.quantityInput) this.quantityInput.value = String(this.quantity);
      if (this.quickBuyQty) this.quickBuyQty.textContent = String(this.quantity);
      this.updateQuickBuyTotal();
    }

    updateQuickBuyTotal() {
      if (this.quickBuyTotal) this.quickBuyTotal.textContent = formatMoney(this.unitPrice * this.quantity);
    }

    setMediaExpanded(expanded, trigger = null, restoreFocus = true, {skipHistory = false} = {}) {
      if (expanded && trigger) this.zoomReturnTarget = trigger;
      this.quickBuy.classList.toggle('is-media-expanded', expanded);
      this.quickBuy.querySelectorAll('[data-quick-buy-zoom]').forEach((button) => {
        button.setAttribute('aria-expanded', String(expanded));
      });

      if (!skipHistory) {
        if (expanded && !this.zoomHistoryPushed) {
          this.zoomHistoryPushed = true;
          window.history.pushState({dikQuickBuyZoom: true}, '');
        } else if (!expanded && this.zoomHistoryPushed) {
          this.zoomHistoryPushed = false;
          window.history.back();
        }
      }

      window.requestAnimationFrame(() => {
        if (expanded) {
          this.quickBuyZoomExit?.focus({preventScroll: true});
        } else if (restoreFocus && this.zoomReturnTarget?.isConnected) {
          this.zoomReturnTarget.focus({preventScroll: true});
        }
      });
    }

    setQuickBuyOpen(open) {
      window.clearTimeout(this.quickBuyCloseTimer);

      if (open) {
        this.setMediaExpanded(false, null, false);
        if (!this.isQuickBuyScrollLocked) {
          lockQuickBuyScroll();
          this.isQuickBuyScrollLocked = true;
        }
        this.quickBuy.hidden = false;
        this.quickBuy.toggleAttribute('open', true);
        window.requestAnimationFrame(() => this.quickBuy.classList.add('is-open'));
        window.requestAnimationFrame(() => {
          this.quickBuy.querySelector('[data-quick-buy-close]')?.focus();
        });
      } else {
        if (this.quickBuy.hidden) return;
        this.setMediaExpanded(false, null, false);
        this.quickBuy.classList.remove('is-open');
        this.quickBuy.removeAttribute('open');
        this.quickBuyCloseTimer = window.setTimeout(() => {
          this.quickBuy.hidden = true;
          if (this.isQuickBuyScrollLocked) {
            unlockQuickBuyScroll();
            this.isQuickBuyScrollLocked = false;
          }
        }, readMotionDuration('--dropdown-close-dur', 220));
        this.toggleButton.focus({preventScroll: true});
      }
    }

    checkout() {
      this.setQuickBuyOpen(false);
      this.form.requestSubmit(this.submitButton);
    }
  }

  // Infinite carousel for product cards, the quick-buy sheet and the PDP
  // gallery. The offset is a single number owned here and every slide is placed
  // at its position modulo the loop length, so the sequence has no ends: there
  // is no edge to run into and nothing ever jumps back. Native scrolling can't
  // loop on iOS — a reset has to wait for a swipe to settle, the next swipe
  // lands first, and the track walks into its hard edge.
  //
  // Touch is read from touch events, not pointer events: iOS Safari scrolls the
  // page on any vertical wobble unless touchmove is cancelled, and only a
  // non-passive touchmove can cancel it. The first few pixels pick the axis —
  // horizontal holds the page still, vertical is left to the browser.
  const LOOP_AXIS_SLOP = 6; // px of travel before a gesture's axis is decided
  const LOOP_FLICK_VELOCITY = 0.3; // px/ms that turns a short swipe into a slide change
  const LOOP_DURATION = 300;

  const loopMod = (value, length) => ((value % length) + length) % length;

  class DikLoopCarousel {
    constructor({track, minSlides = 2, cloneSlide = null, beforeLayout = () => {}, onChange = () => {}}) {
      this.track = track;
      this.minSlides = minSlides;
      this.cloneSlide = cloneSlide;
      this.beforeLayout = beforeLayout;
      this.onChange = onChange;
      this.slides = [];
      this.count = 0;
      this.width = 0;
      this.offset = 0;
      this.target = null;
      this.frame = null;
      this.touch = null;
      this.logical = 0;
      this.suppressClickUntil = 0;
      this.wheel = {at: 0, delta: 0, moved: false};

      track.classList.add('dik-loop-track');
      this.collectSlides();

      track.addEventListener('touchstart', (event) => this.onTouchStart(event), {passive: true});
      track.addEventListener('touchmove', (event) => this.onTouchMove(event), {passive: false});
      track.addEventListener('touchend', (event) => this.onTouchEnd(event), {passive: true});
      track.addEventListener('touchcancel', (event) => this.onTouchEnd(event), {passive: true});
      track.addEventListener('wheel', (event) => this.onWheel(event), {passive: false});
      // A swipe must not also count as a tap on the slide underneath it.
      track.addEventListener('click', (event) => {
        if (performance.now() > this.suppressClickUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, true);

      // Dawn's variant media switch moves the selected image to the front.
      this.mutationObserver = new MutationObserver(() => this.rebuild());
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.connect();
    }

    connect() {
      this.mutationObserver.observe(this.track, {childList: true});
      this.resizeObserver.observe(this.track);
      this.layout();
    }

    disconnect() {
      this.stop();
      this.mutationObserver.disconnect();
      this.resizeObserver.disconnect();
    }

    // Loop over the slides that are actually rendered (Dawn hides extra variant
    // images). Duplicate them when there are too few to fill the view with one
    // to spare, e.g. two photos in the three-up desktop gallery.
    collectSlides() {
      this.track.querySelectorAll(':scope > [data-dik-loop-clone]').forEach((clone) => clone.remove());
      const originals = Array.from(this.track.children).filter((child) => getComputedStyle(child).display !== 'none');
      this.count = originals.length;
      this.slides = originals.slice();
      if (!this.cloneSlide || this.count < 2) return;
      while (this.slides.length < this.minSlides) {
        originals.forEach((slide) => {
          const clone = this.cloneSlide(slide);
          clone.setAttribute('data-dik-loop-clone', '');
          this.track.append(clone);
          this.slides.push(clone);
        });
      }
    }

    rebuild() {
      this.mutationObserver.disconnect();
      this.stop();
      this.slides.forEach((slide) => slide.style.removeProperty('transform'));
      this.collectSlides();
      this.mutationObserver.takeRecords();
      this.mutationObserver.observe(this.track, {childList: true});
      this.touch = null;
      this.target = null;
      this.offset = 0;
      this.width = 0;
      this.layout();
    }

    layout() {
      if (this.touch) return;
      this.beforeLayout();
      const width = this.slides[0]?.getBoundingClientRect().width || 0;
      if (!width || this.count < 2) return;
      const index = this.width ? this.currentIndex() : 0;
      this.stop();
      this.width = width;
      this.target = index;
      this.finish();
      this.emitChange(index);
    }

    // Each slide sits at its natural flex position; shift it to where it
    // belongs in the loop, within one slide left of the view.
    render() {
      const loop = this.slides.length * this.width;
      this.slides.forEach((slide, i) => {
        const x = loopMod(i * this.width + this.offset + this.width, loop) - this.width;
        slide.style.transform = `translate3d(${x - i * this.width}px, 0, 0)`;
      });
    }

    currentIndex() {
      return this.target ?? Math.round(-this.offset / this.width);
    }

    emitChange(index) {
      const logical = loopMod(index, this.count);
      if (logical === this.logical) return;
      this.logical = logical;
      this.onChange(logical);
    }

    animateTo(index) {
      if (!this.width) return;
      this.stop();
      this.target = index;
      this.emitChange(index);
      const from = this.offset;
      const to = -index * this.width;
      if (Math.abs(to - from) < 0.5 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.finish();
        return;
      }
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, Math.max(0, (now - start) / LOOP_DURATION));
        this.offset = from + (to - from) * (1 - (1 - progress) ** 3);
        this.render();
        if (progress < 1) this.frame = window.requestAnimationFrame(tick);
        else this.finish();
      };
      this.frame = window.requestAnimationFrame(tick);
    }

    // Park on the target, folded into the first loop: same pixels, and the
    // offset never grows without bound.
    finish() {
      this.frame = null;
      this.offset = -loopMod(this.target, this.slides.length) * this.width;
      this.target = null;
      this.render();
    }

    stop() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    move(direction) {
      if (!this.width || this.touch) return;
      this.animateTo(this.currentIndex() + direction);
    }

    goToLogical(logical) {
      if (!this.width || this.touch) return;
      const index = this.currentIndex();
      let delta = loopMod(logical - index, this.count);
      if (delta > this.count / 2) delta -= this.count;
      this.animateTo(index + delta);
    }

    onTouchStart(event) {
      if (!this.width) return;
      if (event.touches.length > 1) {
        if (this.touch) this.release();
        return;
      }
      this.stop();
      this.target = null;
      const point = event.touches[0];
      this.touch = {
        x: point.clientX,
        y: point.clientY,
        origin: this.offset,
        axis: null,
        lastX: point.clientX,
        lastTime: performance.now(),
        velocity: 0,
      };
    }

    onTouchMove(event) {
      const touch = this.touch;
      if (!touch) return;
      if (event.touches.length > 1) {
        this.release();
        return;
      }
      const point = event.touches[0];
      const dx = point.clientX - touch.x;
      if (!touch.axis) {
        const dy = point.clientY - touch.y;
        if (Math.abs(dx) < LOOP_AXIS_SLOP && Math.abs(dy) < LOOP_AXIS_SLOP) return;
        if (Math.abs(dx) <= Math.abs(dy) || !event.cancelable) {
          this.release();
          return;
        }
        touch.axis = 'x';
      }
      if (event.cancelable) event.preventDefault();
      const now = performance.now();
      if (now > touch.lastTime) touch.velocity = (point.clientX - touch.lastX) / (now - touch.lastTime);
      touch.lastX = point.clientX;
      touch.lastTime = now;
      this.offset = touch.origin + dx;
      this.render();
    }

    // Not our gesture (vertical scroll, pinch): settle anything it interrupted.
    release() {
      this.touch = null;
      this.animateTo(Math.round(-this.offset / this.width));
    }

    onTouchEnd(event) {
      const touch = this.touch;
      if (!touch || event.touches.length) return;
      this.touch = null;
      const position = -this.offset / this.width;
      if (touch.axis !== 'x') {
        this.animateTo(Math.round(position));
        return;
      }
      this.suppressClickUntil = performance.now() + 400;
      const fresh = event.type === 'touchend' && performance.now() - touch.lastTime < 100;
      let index = Math.round(position);
      if (fresh && Math.abs(touch.velocity) > LOOP_FLICK_VELOCITY) {
        index = touch.velocity < 0 ? Math.ceil(position) : Math.floor(position);
      }
      // One slide per swipe, counted from where the finger landed.
      const start = Math.round(-touch.origin / this.width);
      this.animateTo(Math.max(start - 1, Math.min(start + 1, index)));
    }

    onWheel(event) {
      if (!this.width || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      // One slide per trackpad swipe: a gesture, momentum tail included, is a
      // run of wheel events less than 200ms apart.
      const now = performance.now();
      if (now - this.wheel.at > 200) this.wheel = {at: now, delta: 0, moved: false};
      this.wheel.at = now;
      if (this.wheel.moved) return;
      this.wheel.delta += event.deltaX;
      if (Math.abs(this.wheel.delta) < 30) return;
      this.wheel.moved = true;
      this.move(this.wheel.delta > 0 ? 1 : -1);
    }
  }

  class DikCardCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') {
        this.loop?.connect();
        return;
      }
      this.dataset.ready = 'true';

      this.track = this.querySelector('.dik-card-carousel__track');
      const slides = this.querySelectorAll('.dik-card-carousel__slide');
      this.dots = Array.from(this.querySelectorAll('.dik-card-carousel__dot'));
      this.prevButton = this.querySelector('.dik-card-carousel__arrow--prev');
      this.nextButton = this.querySelector('.dik-card-carousel__arrow--next');

      if (!this.track || slides.length < 2) return;

      this.activeIndex = 0;
      this.loop = new DikLoopCarousel({
        track: this.track,
        beforeLayout: () => this.syncSlideWidth(),
        onChange: (index) => this.setActiveIndex(index),
      });

      // The track sits above the card's stretched product link so it can take
      // swipes (see dik-concept.css); a plain tap still opens the product. The
      // carousel swallows the click that ends a swipe before it gets here.
      this.track.addEventListener('click', () => {
        this.closest('.card')?.querySelector('.card__heading a[href]')?.click();
      });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.loop.goToLogical(Number(dot.dataset.index));
        });
      });

      this.prevButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.loop.move(-1);
      });

      this.nextButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.loop.move(1);
      });
    }

    disconnectedCallback() {
      this.loop?.disconnect();
    }

    syncSlideWidth() {
      const width = this.track?.getBoundingClientRect().width || 0;
      if (!width) return;

      /* Make each slide the next whole pixel wider than a fractional grid cell,
         then let the carousel clip the subpixel excess. There is no uncovered
         edge at 80%/90% browser zoom and no visible image scaling. */
      const pixelWidth = Math.ceil(width - 0.01);
      this.track.style.setProperty('--dik-carousel-slide-width', `${pixelWidth}px`);
    }

    setActiveIndex(index) {
      if (this.activeIndex === index) return;
      this.activeIndex = index;
      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
      });
    }
  }

  class DikProductCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.product__media-list');
      this.prevButton = root.querySelector('[data-dik-pdp-carousel-prev]');
      this.nextButton = root.querySelector('[data-dik-pdp-carousel-next]');
      const originals = this.track?.querySelectorAll(':scope > .product__media-item') || [];
      if (!this.track || originals.length < 2 || !this.prevButton || !this.nextButton) return;

      this.loop = new DikLoopCarousel({
        track: this.track,
        // The three-up desktop view needs one photo to spare while dragging.
        minSlides: 4,
        cloneSlide: (slide) => this.createClone(slide),
      });
      this.prevButton.addEventListener('click', () => this.loop.move(-1));
      this.nextButton.addEventListener('click', () => this.loop.move(1));
      this.root.enableSliderLooping = true;
    }

    createClone(original) {
      const clone = original.cloneNode(true);
      // Dawn shows a variant image only as the list's first child; a copy of
      // one must stay visible wherever it lands.
      clone.classList.remove('is-active', 'product__media-item--variant');
      clone.classList.add('dik-pdp-carousel__clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.removeAttribute('data-media-id');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('[data-media-id]').forEach((element) => element.removeAttribute('data-media-id'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      clone.addEventListener('click', (event) => {
        if (!event.target.closest('.product__modal-opener')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        original.querySelector('.product__modal-opener button')?.click();
      }, true);
      return clone;
    }

  }

  if (!customElements.get('dik-catalog-navigator')) {
    customElements.define('dik-catalog-navigator', DikCatalogNavigator);
  }
  if (!customElements.get('dik-localization')) {
    customElements.define('dik-localization', DikLocalization);
  }
  if (!customElements.get('dik-quick-add')) {
    customElements.define('dik-quick-add', DikQuickAdd);
  }
  if (!customElements.get('dik-card-carousel')) {
    customElements.define('dik-card-carousel', DikCardCarousel);
  }

  const initializeProductCarousels = (scope = document) => {
    scope.querySelectorAll('[data-dik-product-carousel]').forEach((carousel) => {
      if (carousel.dataset.dikCarouselReady === 'true') return;
      carousel.dataset.dikCarouselReady = 'true';
      carousel.dikProductCarousel = new DikProductCarousel(carousel);
    });
  };

  let pdpCardOutsideClickBound = false;

  const initializeMotionHooks = (scope = document) => {
    if (!pdpCardOutsideClickBound) {
      pdpCardOutsideClickBound = true;
      document.addEventListener('click', (event) => {
        document.querySelectorAll('details.t-acc.dik-pdp-card[data-open="true"]').forEach((accordion) => {
          if (accordion.contains(event.target)) return;
          if (typeof accordion.dikSetOpen === 'function') accordion.dikSetOpen(false);
        });
      });
    }

    scope.querySelectorAll('[data-dik-menu-icon]').forEach((icon) => {
      if (icon.dataset.motionReady === 'true') return;
      icon.dataset.motionReady = 'true';
      const details = icon.closest('details');
      if (!details) return;
      const sync = () => {
        icon.dataset.state = details.open ? 'b' : 'a';
      };
      details.addEventListener('toggle', sync);
      sync();
    });

    scope.querySelectorAll('details.t-acc').forEach((accordion) => {
      if (accordion.dataset.motionReady === 'true') return;
      accordion.dataset.motionReady = 'true';
      const head = accordion.querySelector(':scope > .t-acc-head');
      if (!head) return;
      let closeTimer = null;

      const setOpen = (open) => {
        window.clearTimeout(closeTimer);
        if (open) {
          accordion.open = true;
          window.requestAnimationFrame(() => accordion.setAttribute('data-open', 'true'));
        } else {
          accordion.setAttribute('data-open', 'false');
          closeTimer = window.setTimeout(() => {
            accordion.open = false;
          }, readMotionDuration('--acc-collapse', 250));
        }
        head.setAttribute('aria-expanded', String(open));
      };

      accordion.dikSetOpen = setOpen;
      accordion.setAttribute('data-open', String(accordion.open));
      head.setAttribute('aria-expanded', String(accordion.open));
      head.addEventListener('click', (event) => {
        event.preventDefault();
        const nextOpen = accordion.getAttribute('data-open') !== 'true';

        if (nextOpen && accordion.matches('.dik-pdp-card')) {
          const infoContainer = accordion.closest('.product__info-container') || document;
          infoContainer.querySelectorAll('details.t-acc.dik-pdp-card').forEach((sibling) => {
            if (sibling !== accordion && typeof sibling.dikSetOpen === 'function') {
              sibling.dikSetOpen(false);
            }
          });
        }

        setOpen(nextOpen);
      });

      if (accordion.matches('.dik-pdp-size-options')) {
        const valueEl = accordion.querySelector('[data-size-value]');
        if (valueEl) {
          accordion.addEventListener('change', (event) => {
            const input = event.target.closest('input[type="radio"]');
            if (!input) return;
            valueEl.textContent = input.value;
          });
        }
      }
    });
  };

  initializeProductCarousels();
  initializeMotionHooks();
  document.addEventListener('shopify:section:load', (event) => {
    initializeProductCarousels(event.target);
    initializeMotionHooks(event.target);
  });
})();
